import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Send, ChevronLeft, Phone, Video, MoreVertical } from "lucide-react";
import { logger } from "../../utils/logger";
import { scormService } from "../../utils/scormService";
import { ChatMessageContent } from "./ChatMessageContent";
import { OtpNotification } from "./OtpNotification";
import { FakeLandingPage } from "./FakeLandingPage";
import type { LandingPage, LandingPageSubmission } from "./FakeLandingPage";
import { SmishingInputHelper } from "./SmishingInputHelper";
import { buildSmishingInitRequest, buildSmishingMessageRequest, type SmishingChatInitResponse, type SmishingChatReplyResponse, type SmishingRequestCategory } from "./smishing-chat-api";

type WhatsAppStatus = "idle" | "sending" | "receiving" | "ending" | "error" | "finished";

interface WhatsAppMessage {
  role: "user" | "assistant";
  content: string;
  timestamp?: string;
}

interface WhatsAppChatProps {
  microlearningId: string;
  language?: string;
  endpoint?: string;
  senderName?: string;
  senderNumber?: string;
  startLabel?: string;
  statusTexts?: Partial<Record<WhatsAppStatus, string>>;
  linkWarningTitle?: string;
  linkWarningBody?: string;
  otpTexts?: { codeLabel?: string; codeSuffix?: string; backLabel?: string; backHint?: string };
  landingPageTexts?: { backLabel?: string };
  uiTexts?: {
    now?: string;
    inputPlaceholder?: string;
    online?: string;
    typing?: string;
    unknownSender?: string;
    startChat?: string;
    chatEnded?: string;
    errorGeneric?: string;
    errorStartChat?: string;
    errorGetReply?: string;
  };
  embedded?: boolean;
  className?: string;
  onStatusChange?: (status: WhatsAppStatus) => void;
  onCompletionChange?: (completed: boolean) => void;
}

const FALLBACK_SMISHING_ENDPOINT = "https://agentic-ai-agent.keepnetlabs.com/smishing/chat";
const DEFAULT_SMISHING_ENDPOINT = typeof window !== "undefined" && (
  window.location.hostname === "localhost" ||
  window.location.hostname === "127.0.0.1"
) ? "http://localhost:4111/smishing/chat" : FALLBACK_SMISHING_ENDPOINT;

const getAccessToken = (): string | null => {
  if (typeof window === "undefined") return null;
  return new URLSearchParams(window.location.search).get("accessToken");
};

const formatTime = () => {
  const now = new Date();
  return now.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
};

// WhatsApp typing animation
function TypingBubble() {
  return (
    <div className="flex justify-start">
      <div className="relative rounded-lg rounded-tl-none bg-white px-2.5 py-2 shadow-sm">
        <div className="absolute -left-1.5 top-0 h-0 w-0 border-r-[6px] border-t-[6px] border-r-transparent border-t-white" />
        <div className="flex items-center gap-1">
          <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-gray-400 [animation-delay:0ms]" />
          <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-gray-400 [animation-delay:200ms]" />
          <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-gray-400 [animation-delay:400ms]" />
        </div>
      </div>
    </div>
  );
}

export function WhatsAppChat({
  microlearningId,
  language = "en-gb",
  endpoint = DEFAULT_SMISHING_ENDPOINT,
  senderName,
  statusTexts,
  linkWarningTitle,
  linkWarningBody,
  otpTexts,
  landingPageTexts,
  uiTexts,
  embedded = false,
  className = "",
  onStatusChange,
  onCompletionChange
}: WhatsAppChatProps) {
  const [status, setStatus] = useState<WhatsAppStatus>("idle");
  const [messages, setMessages] = useState<WhatsAppMessage[]>([]);
  const [inputValue, setInputValue] = useState("");
  const [hasStarted, setHasStarted] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [otpData, setOtpData] = useState<{ code: string; sender: string } | null>(null);
  const [expectedResponseCategory, setExpectedResponseCategory] = useState<SmishingRequestCategory | undefined>(undefined);
  const shownOtpCodes = useRef<Set<string>>(new Set());
  const activeOtpRef = useRef<{ code: string; sender: string } | null>(null);
  const [activeLandingPage, setActiveLandingPage] = useState<LandingPage | null>(null);
  const landingPageDataRef = useRef<LandingPage | null>(null);
  const pendingLandingPageRef = useRef<LandingPageSubmission | null>(null);
  const clickedLinkRef = useRef<string | null>(null);
  const autoSendRef = useRef(false);
  const [isOpen, setIsOpen] = useState(false);
  const [isInitializing, setIsInitializing] = useState(false);
  const [isFinished, setIsFinished] = useState(false);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const initRequestedRef = useRef(false);

  // Get user info from SCORM LMS
  const studentInfo = useMemo(() => scormService.getStudentInfo(), []);
  const userName = studentInfo.name || "You";
  const resolvedSenderName = senderName || uiTexts?.unknownSender || "Unknown";
  const userInitial = userName.charAt(0).toUpperCase();
  const senderInitial = resolvedSenderName.charAt(0).toUpperCase();

  const updateStatus = useCallback((next: WhatsAppStatus) => {
    setStatus(next);
    onStatusChange?.(next);
  }, [onStatusChange]);

  const statusLabel = useMemo(() => {
    const defaultLabels: Record<WhatsAppStatus, string> = {
      idle: uiTexts?.startChat || "Tap to start",
      sending: "Sending\u2026",
      receiving: "typing\u2026",
      ending: "Ending\u2026",
      finished: uiTexts?.chatEnded || "Chat ended",
      error: uiTexts?.errorGeneric || "Something went wrong"
    };
    return statusTexts?.[status] ?? statusTexts?.idle ?? defaultLabels[status];
  }, [status, statusTexts, uiTexts]);

  const scrollToBottom = useCallback(() => {
    if (!scrollRef.current) return;
    scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  const initChat = useCallback(async () => {
    if (hasStarted || isInitializing) return;
    setIsInitializing(true);
    updateStatus("sending");
    setErrorMessage(null);
    try {
      const token = getAccessToken();
      const headers: Record<string, string> = { "Content-Type": "application/json" };
      if (token) headers["X-AGENTIC-ALLY-TOKEN"] = token;

      const response = await fetch(endpoint, {
        method: "POST",
        headers,
        body: JSON.stringify(buildSmishingInitRequest(microlearningId, language, "whatsapp"))
      });
      const data = await response.json() as SmishingChatInitResponse;
      if (!response.ok || !data.success) {
        throw new Error(data.error || `Init failed: ${response.status}`);
      }
      if (data.firstMessage) {
        setMessages([{ role: "assistant" as const, content: data.firstMessage, timestamp: formatTime() }]);
      }
      // Build-time OTP: show notification shortly after chat opens
      if (data.otpCode && data.otpSender && !shownOtpCodes.current.has(data.otpCode)) {
        shownOtpCodes.current.add(data.otpCode);
        setOtpData({ code: data.otpCode, sender: data.otpSender });
        activeOtpRef.current = { code: data.otpCode, sender: data.otpSender };
      }
      if (data.landingPage) {
        landingPageDataRef.current = data.landingPage;
      }
      if (data.isFinished) {
        setExpectedResponseCategory(undefined);
        setIsFinished(true);
        updateStatus("finished");
        onCompletionChange?.(true);
      } else {
        setExpectedResponseCategory(data.expectedResponseCategory);
        setHasStarted(true);
        updateStatus("idle");
      }
    } catch (error) {
      updateStatus("error");
      setErrorMessage(uiTexts?.errorStartChat || "Failed to start chat.");
      logger.push({
        level: "error",
        code: "WHATSAPP_INIT_FAIL",
        message: "Failed to init whatsapp chat",
        detail: String(error)
      });
    } finally {
      setIsInitializing(false);
    }
  }, [endpoint, hasStarted, isInitializing, language, microlearningId, onCompletionChange, uiTexts?.errorStartChat, updateStatus]);

  useEffect(() => {
    if (initRequestedRef.current) return;
    initRequestedRef.current = true;
    initChat();
  }, [initChat]);

  const sendMessage = useCallback(async () => {
    const trimmed = inputValue.trim();
    if (!trimmed || status !== "idle" || !hasStarted || isFinished) return;
    const nextMessages: WhatsAppMessage[] = [...messages, { role: "user" as const, content: trimmed, timestamp: formatTime() }];
    setMessages(nextMessages);
    setInputValue("");
    setExpectedResponseCategory(undefined);
    updateStatus("receiving");
    setErrorMessage(null);

    try {
      const token = getAccessToken();
      const headers: Record<string, string> = { "Content-Type": "application/json" };
      if (token) headers["X-AGENTIC-ALLY-TOKEN"] = token;

      const response = await fetch(endpoint, {
        method: "POST",
        headers,
        body: JSON.stringify(buildSmishingMessageRequest({
          microlearningId,
          language,
          channel: "whatsapp",
          messages: nextMessages,
          pendingOtp: activeOtpRef.current,
          pendingLandingPageSubmission: pendingLandingPageRef.current,
          pendingClickedLink: clickedLinkRef.current,
        }))
      });
      pendingLandingPageRef.current = null;
      clickedLinkRef.current = null;
      const data = await response.json() as SmishingChatReplyResponse;
      if (!response.ok || !data.success) {
        throw new Error(data.error || `Chat failed: ${response.status}`);
      }
      const replyParts = data.replies && data.replies.length > 1 ? data.replies : data.reply ? [data.reply] : [];
      if (replyParts.length > 0) {
        // First message immediately
        let accumulated: WhatsAppMessage[] = [...nextMessages, { role: "assistant" as const, content: replyParts[0], timestamp: formatTime() }];
        setMessages(accumulated);

        // Subsequent messages with staggered delay (typing effect)
        for (let i = 1; i < replyParts.length; i++) {
          updateStatus("receiving");
          await new Promise(resolve => setTimeout(resolve, 1500 + Math.random() * 1500));
          accumulated = [...accumulated, { role: "assistant" as const, content: replyParts[i], timestamp: formatTime() }];
          setMessages(accumulated);
        }
      }
      // Trigger OTP notification if backend sent one
      if (data.otpCode && data.otpSender && !shownOtpCodes.current.has(data.otpCode)) {
        shownOtpCodes.current.add(data.otpCode);
        setOtpData({ code: data.otpCode, sender: data.otpSender });
        activeOtpRef.current = { code: data.otpCode, sender: data.otpSender };
      }
      // Store landing page data — shown only when user taps the link
      landingPageDataRef.current = data.landingPage || null;
      if (data.isFinished) {
        setExpectedResponseCategory(undefined);
        setIsFinished(true);
        updateStatus("finished");
        onCompletionChange?.(true);
      } else {
        setExpectedResponseCategory(data.expectedResponseCategory);
        updateStatus("idle");
      }
    } catch (error) {
      updateStatus("error");
      setErrorMessage(uiTexts?.errorGetReply || "Failed to get reply.");
      logger.push({
        level: "error",
        code: "WHATSAPP_CHAT_FAIL",
        message: "Failed to fetch whatsapp reply",
        detail: String(error)
      });
    }
  }, [endpoint, hasStarted, inputValue, isFinished, language, messages, microlearningId, onCompletionChange, status, uiTexts?.errorGetReply, updateStatus]);

  const handleLinkClick = useCallback((url: string): boolean => {
    clickedLinkRef.current = url;
    if (landingPageDataRef.current && landingPageDataRef.current.url === url) {
      setActiveLandingPage(landingPageDataRef.current);
      return true;
    }
    return false;
  }, []);

  const handleLandingPageSubmit = useCallback((submission: LandingPageSubmission) => {
    pendingLandingPageRef.current = submission;
    clickedLinkRef.current = null;
    setActiveLandingPage(null);
    setInputValue(Object.values(submission.fields).join(", "));
    autoSendRef.current = true;
  }, []);

  const handleLandingPageBack = useCallback(() => {
    setActiveLandingPage(null);
  }, []);

  // Auto-send after landing page form submission
  useEffect(() => {
    if (autoSendRef.current && inputValue.trim()) {
      autoSendRef.current = false;
      sendMessage();
    }
  }, [inputValue, sendMessage]);

  const isBusy = status === "sending" || status === "receiving" || isInitializing || isFinished;
  const previewText = messages[0]?.content || statusLabel;
  const wrapperClassName = embedded
    ? `w-full ${className}`
    : `w-full max-w-[420px] glass-border-3 p-4 ${className}`;
  const chatShellClassName = embedded
    ? "flex h-full min-h-0 flex-col overflow-hidden rounded-lg"
    : "flex h-full min-h-[420px] flex-col overflow-hidden rounded-lg";

  return (
    <div className={wrapperClassName}>
      {!isOpen ? (
        <div className="relative flex min-h-[380px] flex-col items-center justify-center">
          {/* WhatsApp-style notification card */}
          <button
            type="button"
            onClick={() => {
              setIsOpen(true);
              if (!hasStarted) {
                initChat();
              }
            }}
            className="group relative w-full max-w-[270px] overflow-hidden rounded-[18px] border border-white/30 bg-white/80 p-3 text-left shadow-[0_4px_24px_rgba(0,0,0,0.08)] backdrop-blur-xl transition-all duration-200 hover:scale-[1.01] hover:shadow-[0_8px_32px_rgba(0,0,0,0.12)] active:scale-[0.99] dark:border-white/10 dark:bg-[#2C2C2E]/90 dark:shadow-[0_4px_24px_rgba(0,0,0,0.3)]"
          >
            {/* Header: WhatsApp icon + WhatsApp + now */}
            <div className="relative flex items-center gap-2">
              {/* WhatsApp app icon */}
              <div className="relative flex h-9 w-9 shrink-0 items-center justify-center rounded-[10px] bg-[#25D366] shadow-sm">
                <svg viewBox="0 0 24 24" className="h-5 w-5" fill="white">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                </svg>
              </div>
              <span className="text-[13px] font-semibold text-[#1C1C1E] dark:text-[#F2F2F7]">
                WhatsApp
              </span>
              <span className="ml-auto text-[12px] text-[#8E8E93] dark:text-[#98989D]">
                {uiTexts?.now || "now"}
              </span>
            </div>

            {/* Sender name & message */}
            <div className="relative mt-1.5 pl-[44px]">
              <div className="flex items-center justify-between">
                <span className="text-[14px] font-semibold text-[#1C1C1E] dark:text-[#F2F2F7]">
                  {resolvedSenderName}
                </span>
                {/* Unread badge */}
                <span className="flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-[#25D366] px-1.5 text-[11px] font-bold text-white">
                  1
                </span>
              </div>
              {/* Message preview */}
              <div className="mt-0.5 pr-3 text-[14px] leading-snug text-[#3C3C43]/70 line-clamp-2 dark:text-[#EBEBF5]/60">
                {previewText}
              </div>
            </div>
          </button>

          {/* Home indicator */}
          <div className="absolute bottom-2 left-1/2 -translate-x-1/2">
            <div className="h-1 w-28 rounded-full bg-black/15 dark:bg-white/20" />
          </div>
        </div>
      ) : (
        <div className={`${chatShellClassName} relative`}>
          {/* OTP Notification overlay */}
          {otpData && (
            <OtpNotification
              otpCode={otpData.code}
              otpSender={otpData.sender}
              codeLabel={otpTexts?.codeLabel}
              codeSuffix={otpTexts?.codeSuffix}
              backLabel={otpTexts?.backLabel}
              backHint={otpTexts?.backHint}
              nowLabel={uiTexts?.now}
            />
          )}
          {activeLandingPage && (
            <FakeLandingPage
              landingPage={activeLandingPage}
              onSubmit={handleLandingPageSubmit}
              onBack={handleLandingPageBack}
              backLabel={landingPageTexts?.backLabel}
            />
          )}
          {/* WhatsApp header - teal */}
          <div className="shrink-0 flex items-center gap-1.5 px-1.5 py-1.5" style={{ backgroundColor: "#075E54" }}>
            <ChevronLeft size={18} className="shrink-0 text-white/80" />
            {/* Contact avatar */}
            <div className="h-7 w-7 shrink-0 rounded-full bg-gray-300 flex items-center justify-center text-xs font-bold text-gray-600">
              {senderInitial}
            </div>
            {/* Name and status */}
            <div className="flex-1 min-w-0">
              <div className="text-[13px] font-medium text-white leading-tight truncate">
                {resolvedSenderName}
              </div>
              <div className="text-[10px] text-white/60 leading-tight">
                {status === "receiving" ? (uiTexts?.typing || "typing...") : (uiTexts?.online || "online")}
              </div>
            </div>
            {/* Action icons + user avatar */}
            <div className="flex items-center gap-2">
              <Video size={16} className="text-white/80" />
              <Phone size={14} className="text-white/80" />
              <MoreVertical size={16} className="text-white/80" />
              <div className="h-6 w-6 shrink-0 rounded-full bg-[#00A884] flex items-center justify-center text-[10px] font-bold text-white">
                {userInitial}
              </div>
            </div>
          </div>

          {/* Chat area - WhatsApp wallpaper */}
          <div
            ref={scrollRef}
            className="min-h-0 flex-1 overflow-y-auto px-2 py-2"
            style={{ backgroundColor: "#ECE5DD" }}
          >
            {/* Today divider */}
            <div className="mb-2 flex items-center justify-center">
              <span className="rounded-md bg-white/80 px-2.5 py-0.5 text-[10px] font-medium text-[#8696A0] shadow-sm">
                TODAY
              </span>
            </div>

            {/* Encryption notice */}
            <div className="mb-2 flex items-center justify-center">
              <div className="rounded-md bg-[#FFF3C4]/80 px-2 py-1 text-center text-[10px] text-[#54656F] shadow-sm">
                <span className="mr-0.5">&#128274;</span>
                Messages are end-to-end encrypted.
              </div>
            </div>

            {messages.length === 0 && !isInitializing && (
              <div className="flex items-center justify-center py-8 text-xs text-[#8696A0]">
                {statusLabel}
              </div>
            )}

            <div className="flex flex-col gap-1.5">
              {messages.map((msg, idx) => (
                <div
                  key={`${msg.role}-${idx}`}
                  className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}
                >
                  <div
                    className={`relative max-w-[80%] break-words [overflow-wrap:anywhere] rounded-lg px-2 pb-1 pt-1.5 shadow-sm ${
                      msg.role === "user"
                        ? "rounded-tr-none bg-[#DCF8C6]"
                        : "rounded-tl-none bg-white"
                    }`}
                  >
                    {/* Message tail */}
                    <div
                      className={`absolute top-0 h-0 w-0 ${
                        msg.role === "user"
                          ? "-right-1.5 border-l-[6px] border-t-[6px] border-l-transparent border-t-[#DCF8C6]"
                          : "-left-1.5 border-r-[6px] border-t-[6px] border-r-transparent border-t-white"
                      }`}
                    />
                    <p className="text-[13px] leading-snug text-[#111B21]">
                      <ChatMessageContent
                        content={msg.content}
                        linkWarningTitle={linkWarningTitle}
                        linkWarningBody={linkWarningBody}
                        onLinkClick={handleLinkClick}
                      />
                    </p>
                    {/* Timestamp + read ticks */}
                    <div className="mt-0.5 flex items-center justify-end gap-0.5">
                      <span className="text-[9px] text-[#667781]">
                        {msg.timestamp}
                      </span>
                      {msg.role === "user" && (
                        <svg viewBox="0 0 16 11" className="h-[10px] w-3.5 text-[#53BDEB]" fill="currentColor">
                          <path d="M11.071.653a.457.457 0 0 0-.304-.102.493.493 0 0 0-.381.178l-6.19 7.636-2.011-2.175a.463.463 0 0 0-.336-.153.457.457 0 0 0-.336.153.44.44 0 0 0-.142.334.44.44 0 0 0 .142.334l2.39 2.588a.456.456 0 0 0 .685-.012l6.524-8.058a.444.444 0 0 0 .102-.382.44.44 0 0 0-.143-.341z" />
                          <path d="M14.757.653a.457.457 0 0 0-.304-.102.493.493 0 0 0-.381.178l-6.19 7.636-1.2-1.298-.389.483 1.553 1.681a.456.456 0 0 0 .685-.012l6.524-8.058a.444.444 0 0 0-.298-.508z" />
                        </svg>
                      )}
                    </div>
                  </div>
                </div>
              ))}
              {status === "receiving" && <TypingBubble />}
            </div>
          </div>

          {/* Input area - compact */}
          <SmishingInputHelper
            category={expectedResponseCategory}
            language={language}
            disabled={isBusy || !hasStarted}
            onSelect={setInputValue}
          />
          <div className="shrink-0 flex items-center gap-1 px-1.5 pb-2 pt-1" style={{ backgroundColor: "#ECE5DD" }}>
            <div className="flex flex-1 items-center rounded-full bg-white px-3 py-1.5 shadow-sm">
              <input
                type="text"
                value={inputValue}
                onChange={(event) => setInputValue(event.target.value)}
                onKeyDown={(event) => event.key === "Enter" && sendMessage()}
                placeholder={hasStarted ? (uiTexts?.inputPlaceholder || "Type a message") : statusLabel}
                disabled={isBusy || !hasStarted}
                className="flex-1 bg-transparent text-[13px] text-[#111B21] placeholder-[#8696A0] focus:outline-none"
              />
            </div>
            <button
              type="button"
              onClick={sendMessage}
              disabled={!inputValue.trim() || isBusy || !hasStarted}
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#00A884] text-white shadow-sm transition-colors disabled:bg-[#00A884]/60"
              aria-label="Send message"
            >
              <Send size={16} />
            </button>
          </div>
        </div>
      )}

      {errorMessage && (
        <div className="mt-2 text-xs text-rose-600 dark:text-rose-300">{errorMessage}</div>
      )}
    </div>
  );
}
